// @ts-strict-ignore
import {AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';
import {TimeUnit, TimeUnitLabelTranslatable} from 'common/widgets/date-range-picker/models/types';
import {VisibilityChangeEvent} from 'common/components/base/stackable';

import {template} from './template';
import css from './index.inline.styl';

const BRACKET_UNITS = Object.freeze({
  items: [
    {
      value: TimeUnit.Day,
      label: ({t}) => t(`query-builder.retention.interval.custom-menu.daily`),
    },
    {
      value: TimeUnit.Week,
      label: ({t}) => t(`query-builder.retention.interval.custom-menu.weekly`),
    },
    {
      value: TimeUnit.Month,
      label: ({t}) => t(`query-builder.retention.interval.custom-menu.monthly`),
    },
  ],
});

// Set at 5 years
const MaxUnit = {
  [TimeUnit.Day]: 5 * 365,
  [TimeUnit.Week]: 5 * 52,
  [TimeUnit.Month]: 5 * 12,
};
const MaxUnitError = {
  [TimeUnit.Day]:
    (count) =>
    ({t}) =>
      t(`query-builder.retention.interval.custom-menu.error.max-days`, {count}),
  [TimeUnit.Week]:
    (count) =>
    ({t}) =>
      t(`query-builder.retention.interval.custom-menu.error.max-weeks`, {count}),
  [TimeUnit.Month]:
    (count) =>
    ({t}) =>
      t(`query-builder.retention.interval.custom-menu.error.max-months`, {count}),
};
export interface Bucket {
  interval: number;
  start: number;
  end: number;
  label: string;
  errorMsg?: string;
  valid: boolean;
  unparsedInterval: string;
}

const DEFAULT_BUCKET: Bucket = Object.freeze({
  interval: null,
  start: null,
  end: null,
  label: `...`,
  valid: false,
  errorMsg: null,
  unparsedInterval: null,
});

interface BucketPaneState {
  bucketUnit: TimeUnit;
  buckets: Bucket[];
  tooltipErrorMsg: string;
}

function firstValidationError(validationFns: Array<() => string>): string {
  for (const validationFn of validationFns) {
    const errorMessage = validationFn();
    if (errorMessage) {
      return errorMessage;
    }
  }
  return null;
}

/** widget for selecting custom bucket sizes and bucket unit */
export class BucketSize extends Component<BucketPaneState, any, unknown, unknown, IronContextRegistry> {
  static get attrsSchema(): AttrsSchema<any> {
    return {
      ...super.attrsSchema,
      'bucket-unit': {
        type: `string`,
        default: TimeUnit,
        enum: [TimeUnit.Day, TimeUnit.Month, TimeUnit.Week],
      },
      'bucket-sizes': {
        type: `json`,
        description: `array of all bucket sizes`,
      },
      header: {
        type: `string`,
        default: `Custom`,
        description: `header text`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      contexts: [`i18n` as const],
      defaultState: {
        bucketUnit: TimeUnit.Day,
        buckets: [{...DEFAULT_BUCKET}],
        tooltipErrorMsg: null,
      },
      helpers: {
        applyBucketSizes: () => {
          const {buckets, bucketUnit} = this.state;
          const appliedBuckets = buckets
            .filter((bucket) => bucket.interval !== null)
            .map((bucket) => ({
              start: bucket.start,
              end: bucket.end,
              interval: bucket.interval,
            }));
          this.dispatchEvent(
            new CustomEvent(`apply`, {
              detail: {
                buckets: appliedBuckets,
                bucketUnit,
              },
            }),
          );
        },
        cancelBucketSizes: () => {
          this.dispatchEvent(new CustomEvent(`cancel`));
        },
        getUnitSelectorProps: () => {
          const sectionItems = BRACKET_UNITS.items.map((sectionItem) => ({
            ...sectionItem,
            isSelected: sectionItem.value === this.state.bucketUnit,
            label: this.getContext(`i18n`).print(sectionItem.label),
          }));

          return {
            sections: [{items: sectionItems}],
          };
        },
        handleChangeUnitSelector: (ev) => {
          const {buckets} = this.state;
          const bucketUnit = ev.detail.item.value;
          this.calculateBuckets(buckets, bucketUnit);

          // when changing units and there is a new bucket added but the second to last bucket has an error pop that bucket
          // or if there are no errors for last bucket then add a new line. This is common when bucket either fits or is too
          // big for one unit's max unit but no another unit's max
          if (
            buckets.length > 2 &&
            buckets[buckets.length - 1].interval === null &&
            !buckets[buckets.length - 2].valid
          ) {
            buckets.pop();
          } else if (buckets[buckets.length - 1].interval !== null && buckets[buckets.length - 1].valid) {
            buckets.push({...DEFAULT_BUCKET});
          }

          const tooltipErrorMsg = this.findTooltipErrorMsg(
            buckets.indexOf(this.findClosestNonNullBucket(buckets.length - 1, buckets)),
            [...buckets],
          );
          this.update({
            buckets,
            tooltipErrorMsg,
            bucketUnit,
          });
        },
        changeBracketIntervalInput: (ev, index: number) => {
          let {buckets, tooltipErrorMsg} = this.state;
          // checking if user deleted 2nd to last buckets input and the input of the last bucket is null
          if (!ev.target.value && index + 2 === buckets.length && buckets[buckets.length - 1].interval === null) {
            const lastNonNullBucket = this.findClosestNonNullBucket(index, this.state.buckets);
            buckets = buckets.slice(0, buckets.indexOf(lastNonNullBucket) + 1);
            buckets.push({...DEFAULT_BUCKET});
          } else {
            buckets[index].interval = parseInt(ev.target.value);
            buckets[index].unparsedInterval = ev.target.value;
            this.calculateBuckets(buckets, this.state.bucketUnit);
            // add new bucket if user is typing in last bucket and their input is valid or remove last bucket if user
            // is typing in second to last bucket and the input is invalid.
            if (index === buckets.length - 1 && buckets[index].valid) {
              buckets.push({...DEFAULT_BUCKET});
            } else if (index === buckets.length - 2 && !buckets[index].valid && !buckets[index + 1].interval) {
              buckets.pop();
            }
          }

          tooltipErrorMsg = this.findTooltipErrorMsg(index, buckets);

          this.update({buckets, tooltipErrorMsg});
        },
        insertBucketSizeInput: (vnode, index) => {
          vnode.elm.scrollIntoView && vnode.elm.scrollIntoView();
          if (index === 0) {
            // The mp-select for picking the retention unit steals the focus. We need to wait until after that has
            // stolen the focus before focusing the bucket size input
            setTimeout(() => vnode.elm.focus(), 100);
          }
        },
        isBucketValid: (index: number) => {
          const {buckets} = this.state;
          return buckets[index].valid || (index === buckets.length - 1 && buckets[index].interval === null);
        },
        menuChange: (ev: VisibilityChangeEvent) => {
          if (ev.detail.state === `closed`) {
            this.dispatchEvent(new CustomEvent(`cancel`));
          }
        },
        t: (key, options) => this.getContext(`i18n`).t(key, options),
      },
    };
  }

  // Used to find the previous bucket that is not a null bucket (is valid)
  findClosestNonNullBucket(index: number, buckets: Bucket[]): Bucket {
    const bucketsToSearch = buckets.slice(0, index).reverse();
    return bucketsToSearch.find((bucket) => bucket.interval !== null);
  }

  findFirstErrorBucket(buckets: Bucket[]): Bucket {
    return buckets.find((_, index) => !this.helpers.isBucketValid(index));
  }

  findTooltipErrorMsg(index: number, buckets: Bucket[]) {
    let tooltipErrorMsg = buckets[index]?.errorMsg
      ? buckets[index].errorMsg
      : this.findFirstErrorBucket(buckets)?.errorMsg;

    // if the error for the last bucket is that it's not specified, then we do not need to show that error
    if (tooltipErrorMsg === buckets[buckets.length - 1].errorMsg && buckets[buckets.length - 1].interval === null) {
      tooltipErrorMsg = null;
    }

    return tooltipErrorMsg;
  }

  calculateBuckets(buckets: Bucket[], bucketUnit: TimeUnit) {
    const bucketUnitLabel = this.getContext(`i18n`).print(TimeUnitLabelTranslatable[bucketUnit](1));
    let currentBucketEnd = -1;

    for (let i = 0; i < buckets.length; i++) {
      const interval = buckets[i].interval;
      const errorMsg = this.validateNumber(i, buckets[i].unparsedInterval); // need to check validation with unparsed input

      if (errorMsg) {
        buckets[i] = {...DEFAULT_BUCKET};
        buckets[i].errorMsg = errorMsg;
        buckets[i].interval = Number.isNaN(interval) ? null : interval;
      } else if (interval === 1 || interval === 0) {
        currentBucketEnd += interval;
        const errMsg = this.validateMax(i, interval, bucketUnit);
        buckets[i].interval = interval;
        buckets[i].start = interval ? currentBucketEnd : null;
        buckets[i].end = interval ? currentBucketEnd : null;
        buckets[i].label = interval && !errMsg ? `${bucketUnitLabel} ${currentBucketEnd}` : `...`;
        buckets[i].valid = !errMsg;
        buckets[i].errorMsg = errMsg;
      } else {
        const errMsg = this.validateMax(i, interval, bucketUnit);
        buckets[i].start = currentBucketEnd + 1;
        currentBucketEnd += interval;
        buckets[i].end = currentBucketEnd;
        buckets[i].interval = interval;
        buckets[i].label = errMsg ? `...` : `${bucketUnitLabel} ${buckets[i].start}-${buckets[i].end}`;
        buckets[i].valid = !errMsg;
        buckets[i].errorMsg = errMsg;
      }
    }
    return buckets;
  }

  validateNumber(bucketIndex: number, number: string): string {
    const {t} = this.getContext(`i18n`);
    const fieldName = t(`query-builder.retention.interval.custom-menu.bracket-number`, {
      number: bucketIndex + 1,
    });
    return firstValidationError([
      () =>
        !Number.isFinite(parseInt(number))
          ? t(`query-builder.retention.interval.custom-menu.error.not-specified`, {bracket: fieldName})
          : null,
      () =>
        !Number.isInteger(Number(number))
          ? t(`query-builder.retention.interval.custom-menu.error.not-integer`, {bracket: fieldName})
          : null,
      () =>
        Number(number) <= 0
          ? t(`query-builder.retention.interval.custom-menu.error.not-positive`, {bracket: fieldName})
          : null,
    ]);
  }

  validateMax(bucketIndex: number, number: number, bucketUnit: TimeUnit): string {
    return (bucketIndex >= 1 &&
      this.findClosestNonNullBucket(bucketIndex, this.state.buckets)?.end + number > MaxUnit[bucketUnit]) ||
      (bucketIndex === 0 && number > MaxUnit[bucketUnit])
      ? this.getContext(`i18n`).print(MaxUnitError[bucketUnit](MaxUnit[bucketUnit]))
      : null;
  }

  generateEditBuckets(bucketSizes: number[], bucketUnit: TimeUnit) {
    const buckets = bucketSizes.map((size) => {
      return {...DEFAULT_BUCKET, interval: size, unparsedInterval: size.toString()};
    });
    return [...this.calculateBuckets(buckets, bucketUnit), {...DEFAULT_BUCKET}];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    super.attributeChangedCallback(name, oldValue, newValue);

    const bucketUnit = this.attr(`bucket-unit`);
    const buckets =
      this.attr(`bucket-sizes`) && this.attr(`bucket-sizes`).length
        ? this.generateEditBuckets(this.attr(`bucket-sizes`), this.attr(`bucket-unit`))
        : [{...DEFAULT_BUCKET}];

    if (!bucketUnit) {
      return;
    }

    this.update({
      bucketUnit,
      buckets,
    });
  }
}

export const BucketSizeEl = defineAndMakeJsxFactory(`mp-bucket-sizes`, BucketSize);
export const BucketSizeReact = panel2react(`mp-bucket-sizes`, BucketSize);
